import React, { useState, useRef, useEffect } from 'react';
import { 
  User, 
  CreditCard, 
  DollarSign, 
  Upload, 
  ChevronLeft, 
  ChevronRight, 
  Check,
  AlertCircle,
  Camera,
  Receipt,
  Smartphone,
  CheckCircle,
  X,
  Mail,
  Copy
} from 'lucide-react';
import { generateSubmissionConfirmationEmail, generateSubmissionConfirmationText } from '../utils/emailTemplates';
import SecurityBadge from '../components/SecurityBadge';
import PageSeo from '../components/PageSeo';

const FormSubmission = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    
    // Gift Card Details
    cards: [{
      id: Date.now(), // Unique ID for React key
      brand: '',
      value: '',
      condition: '', // new, partially-used
      hasReceipt: '',
      cardType: '', // physical, digital
      submissionMethod: '', // upload, manual
      digitalCode: '',
      digitalPin: '',
      expiryDate: '', // For Visa/Mastercard
      frontImage: null,
      backImage: null,
      receiptImage: null
    }],
    
    // Payment Information
    paymentMethod: '',
    paypalAddress: '',
    zelleDetails: '',
    cashAppTag: '',
    btcAddress: '',
    chimeDetails: ''
  });

  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingSteps, setProcessingSteps] = useState({
    verifying: false,
    security: false,
    encrypting: false,
    complete: false
  });
  
  // OCR state
  const [ocrLoading, setOcrLoading] = useState({});
  const [ocrError, setOcrError] = useState({});
  const [ocrSuccess, setOcrSuccess] = useState({});
  const [ocrData, setOcrData] = useState({}); // Store accumulated OCR data
  
  // Visa/Mastercard inline notice state
  const [showVisaMcNotice, setShowVisaMcNotice] = useState({});

  const steps = [
    {
      id: 1,
      title: 'Personal Information',
      icon: User,
      description: 'Tell us about yourself'
    },
    {
      id: 2,
      title: 'Gift Card Details',
      icon: CreditCard,
      description: 'Enter card details and/or upload images'
    },
    {
      id: 3,
      title: 'Payment Method',
      icon: DollarSign,
      description: 'Choose how you want to receive payment'
    },
    {
      id: 4,
      title: 'Review & Submit',
      icon: CheckCircle,
      description: 'Review your information and submit'
    }
  ];

  // AUTO-SAVE: State for draft restoration
  const [showDraftNotification, setShowDraftNotification] = useState(false);
  const [draftAge, setDraftAge] = useState(null);

  // AUTO-SAVE: Load draft on mount
  useEffect(() => {
    const draft = localStorage.getItem('form_draft');
    const draftTimestamp = localStorage.getItem('form_draft_timestamp');
    
    if (draft && draftTimestamp) {
      try {
        const savedData = JSON.parse(draft);
        const savedTime = new Date(draftTimestamp);
        const now = new Date();
        const minutesAgo = Math.floor((now - savedTime) / 60000);
        
        // Only restore if draft is less than 24 hours old
        if (minutesAgo < 1440) {
          setDraftAge(minutesAgo);
          setShowDraftNotification(true);
          
          // Auto-restore if less than 30 minutes old
          if (minutesAgo < 30) {
            setFormData(savedData);
            console.log('✅ Form draft auto-restored from', minutesAgo, 'minutes ago');
          }
        } else {
          // Clear old draft
          localStorage.removeItem('form_draft');
          localStorage.removeItem('form_draft_timestamp');
        }
      } catch (e) {
        console.error('Error loading draft:', e);
      }
    }
  }, []);

  // AUTO-SAVE: Save form data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      // Only save if user has started filling the form
      if (formData.firstName || formData.email || formData.cards[0].brand) {
        try {
          localStorage.setItem('form_draft', JSON.stringify(formData));
          localStorage.setItem('form_draft_timestamp', new Date().toISOString());
          console.log('💾 Form auto-saved');
        } catch (e) {
          console.error('Error saving draft:', e);
        }
      }
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [formData]);

  // AUTO-SAVE: Clear draft after successful submission
  const clearDraft = () => {
    localStorage.removeItem('form_draft');
    localStorage.removeItem('form_draft_timestamp');
    setShowDraftNotification(false);
  };

  // AUTO-SAVE: Restore draft manually
  const restoreDraft = () => {
    const draft = localStorage.getItem('form_draft');
    if (draft) {
      try {
        const savedData = JSON.parse(draft);
        setFormData(savedData);
        setShowDraftNotification(false);
        console.log('✅ Draft restored manually');
      } catch (e) {
        console.error('Error restoring draft:', e);
      }
    }
  };

  // AUTO-SAVE: Dismiss notification
  const dismissDraft = () => {
    setShowDraftNotification(false);
    localStorage.removeItem('form_draft');
    localStorage.removeItem('form_draft_timestamp');
  };


  // Progress calculation
  const progress = (currentStep / steps.length) * 100;

  // Handle form input changes
  const handleInputChange = (field, value, cardIndex = null) => {
    // Debug logging for mobile troubleshooting
    if (process.env.NODE_ENV === 'development') {
      console.log('📝 Field changed:', field, '=', value, 'cardIndex:', cardIndex);
    }
    
    if (cardIndex !== null) {
      const newCards = [...formData.cards];
      newCards[cardIndex] = { ...newCards[cardIndex], [field]: value };
      setFormData({ ...formData, cards: newCards });
      
      // Show inline notice if brand is Visa or Mastercard
      if (field === 'brand' && value) {
        const lowerValue = value.toLowerCase();
        if (lowerValue.includes('visa') || lowerValue.includes('master')) {
          setShowVisaMcNotice(prev => ({ ...prev, [cardIndex]: true }));
        } else {
          setShowVisaMcNotice(prev => ({ ...prev, [cardIndex]: false }));
        }
      }
    } else {
      setFormData({ ...formData, [field]: value });
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // File upload handler with preview
  const handleFileUpload = (file, field, cardIndex = 0) => {
    if (!file) return;
    
    // Validate file size (15MB)
    if (file.size > 15 * 1024 * 1024) {
      setErrors({ ...errors, [field]: 'File size must be less than 15MB' });
      return;
    }
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrors({ ...errors, [field]: 'Please upload a valid image file (JPEG, PNG, WebP)' });
      return;
    }

    const newCards = [...formData.cards];
    newCards[cardIndex] = { ...newCards[cardIndex], [field]: file };
    setFormData({ ...formData, cards: newCards });
    
    // Clear any previous errors
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Add new card
  const addCard = () => {
    const newCard = {
      id: Date.now() + Math.random(), // Unique ID for React key - prevents re-mounting on mobile
      brand: '',
      value: '',
      condition: '',
      hasReceipt: '',
      cardType: '',
      submissionMethod: '',
      digitalCode: '',
      digitalPin: '',
      expiryDate: '', // For Visa/Mastercard
      frontImage: null,
      backImage: null,
      receiptImage: null
    };
    setFormData({ ...formData, cards: [...formData.cards, newCard] });
  };

  // Remove card
  const removeCard = (index) => {
    if (formData.cards.length > 1) {
      const newCards = formData.cards.filter((_, i) => i !== index);
      setFormData({ ...formData, cards: newCards });
    }
  };

  // Validation functions
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
        break;
        
      case 2:
        // Validate gift card details
        if (!formData.cards.length) newErrors.cards = 'At least one gift card is required';
        
        formData.cards.forEach((card, index) => {
          if (!card.brand.trim()) newErrors[`brand_${index}`] = 'Brand is required';
          if (!card.value || parseFloat(card.value) <= 0) newErrors[`value_${index}`] = 'Valid card value is required';
          if (!card.condition) newErrors[`condition_${index}`] = 'Card condition is required';
          if (!card.hasReceipt) newErrors[`hasReceipt_${index}`] = 'Receipt information is required';
          if (!card.cardType) newErrors[`cardType_${index}`] = 'Card type is required';
          
          // Validate that customer provided EITHER manual entry OR image uploads
          const hasManualEntry = card.digitalCode && card.digitalCode.trim();
          const hasImages = card.frontImage || card.backImage;
          
          if (!hasManualEntry && !hasImages) {
            newErrors[`card_${index}`] = 'Please provide card information by entering details and/or uploading images';
          }
        });
        break;
        
      case 3:
        if (!formData.paymentMethod) newErrors.paymentMethod = 'Please select a payment method';
        // Validate payment method specific fields
        if (formData.paymentMethod === 'paypal' && !formData.paypalAddress.trim()) {
          newErrors.paypalAddress = 'PayPal email is required';
        }
        if (formData.paymentMethod === 'zelle' && !formData.zelleDetails.trim()) {
          newErrors.zelleDetails = 'Zelle details are required';
        }
        if (formData.paymentMethod === 'cashapp' && !formData.cashAppTag.trim()) {
          newErrors.cashAppTag = 'Cash App tag is required';
        }
        if (formData.paymentMethod === 'btc' && !formData.btcAddress.trim()) {
          newErrors.btcAddress = 'Bitcoin wallet address is required';
        }
        if (formData.paymentMethod === 'chime' && !formData.chimeDetails.trim()) {
          newErrors.chimeDetails = 'Chime details are required';
        }
        break;
        
      case 4:
        // Final review step - no additional validation needed
        // All validation is handled in previous steps
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation functions
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
      // Scroll to top of page when moving to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroll to top of page when going back
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateStep(4) || isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Reset processing steps
    setProcessingSteps({
      verifying: false,
      security: false,
      encrypting: false,
      complete: false
    });
    
    try {
        // Show processing animation
        setTimeout(() => setProcessingSteps(prev => ({ ...prev, verifying: true })), 500);
        setTimeout(() => setProcessingSteps(prev => ({ ...prev, security: true })), 1500);
        setTimeout(() => setProcessingSteps(prev => ({ ...prev, encrypting: true })), 2500);
        
        // Get backend URL from environment
        // Use relative URL for production (same domain) or env variable for development
        const backendUrl = window.location.hostname === 'localhost' 
          ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001')
          : '';  // Empty string means relative URL - same domain as frontend
        
        // Convert file objects to base64 for JSON transmission
        const formDataWithFiles = { ...formData };
        
        for (let i = 0; i < formDataWithFiles.cards.length; i++) {
          const card = formDataWithFiles.cards[i];
          
          // Remove the 'id' field used for React keys - not needed in backend
          delete formDataWithFiles.cards[i].id;
          
          // Convert files to base64
          if (card.frontImage && card.frontImage instanceof File) {
            const base64 = await fileToBase64(card.frontImage);
            formDataWithFiles.cards[i].frontImage = {
              name: card.frontImage.name,
              type: card.frontImage.type,
              size: card.frontImage.size,
              data: base64
            };
          }
          
          if (card.backImage && card.backImage instanceof File) {
            const base64 = await fileToBase64(card.backImage);
            formDataWithFiles.cards[i].backImage = {
              name: card.backImage.name,
              type: card.backImage.type,
              size: card.backImage.size,
              data: base64
            };
          }
          
          if (card.receiptImage && card.receiptImage instanceof File) {
            const base64 = await fileToBase64(card.receiptImage);
            formDataWithFiles.cards[i].receiptImage = {
              name: card.receiptImage.name,
              type: card.receiptImage.type,
              size: card.receiptImage.size,
              data: base64
            };
          }
        }

        // Add timeout for mobile networks - increased for large images
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout for large images

        const response = await fetch(`${backendUrl}/api/submit-gift-card`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formDataWithFiles),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Check if response is OK before parsing JSON
        if (!response.ok) {
          const errorText = await response.text();
          let errorDetail = '';
          
          // Try to parse JSON error response for validation errors
          try {
            const errorJson = JSON.parse(errorText);
            if (errorJson.detail) {
              // Pydantic validation errors
              if (Array.isArray(errorJson.detail)) {
                errorDetail = errorJson.detail.map(err => `${err.loc.join('.')} - ${err.msg}`).join('; ');
              } else {
                errorDetail = errorJson.detail;
              }
            }
          } catch (e) {
            // Not JSON, use text as is
            errorDetail = errorText;
          }
          
          throw new Error(`Server error (${response.status}): ${errorDetail || 'Failed to submit form'}`);
        }

        const result = await response.json();
        
        if (result.success) {
          // Mark processing as complete
          setProcessingSteps(prev => ({ ...prev, complete: true }));
          
          setSubmissionResult(result);
          
          // Wait 3 seconds to show processing completion before showing success modal
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          setShowSuccessModal(true);
          
          // AUTO-SAVE: Clear draft after successful submission
          clearDraft();
          
          // Google Ads Conversion Tracking
          if (typeof gtag !== 'undefined') {
            // Track form submission conversion
            gtag('event', 'conversion', {
              'send_to': 'AW-922-336-5292/gift_card_quote_request',
              'value': 1.0,
              'currency': 'USD',
              'transaction_id': result.submissionId || Date.now()
            });
            
            // Track Google Analytics event
            gtag('event', 'quote_request_submitted', {
              'event_category': 'engagement',
              'event_label': 'gift_card_form',
              'value': 1
            });
          }
          
          // Optional: Clear form or redirect
          // setFormData(initialFormData);
          // setCurrentStep(1);
        } else {
          alert(`Error: ${result.message}\n\nPlease try again or contact support if the issue persists.`);
        }
        
      } catch (error) {
        console.error('Submission error:', error);
        
        // Better error handling for mobile with null checks
        let errorMessage = 'There was an error processing your submission.\n\n';
        
        try {
          const errorMsg = error?.message || '';
          const errorName = error?.name || '';
          
          if (errorName === 'TypeError' && errorMsg.includes('fetch')) {
            errorMessage += 'Please check your internet connection and try again.';
          } else if (errorMsg.includes('timeout') || errorName === 'AbortError') {
            errorMessage += 'The request timed out. Please try again with smaller images.';
          } else if (errorMsg.includes('413') || errorMsg.includes('too large')) {
            errorMessage += 'Your images are too large. Please reduce image size and try again.';
          } else if (errorMsg.includes('422') || errorMsg.includes('validation')) {
            // Validation error - show the actual validation message
            errorMessage += 'Validation Error:\n' + errorMsg.split(':').slice(1).join(':').trim();
            errorMessage += '\n\nPlease check your form fields and try again.';
          } else if (errorMsg.includes('500')) {
            errorMessage += 'Server error occurred. Our team has been notified. Please try again in a few minutes.';
          } else if (errorMsg) {
            // Show the actual error message if available
            errorMessage += errorMsg;
          } else {
            errorMessage += 'Please try again or contact support if the issue persists.';
          }
        } catch (e) {
          // If error processing fails, use generic message
          errorMessage += 'Please try again or contact support if the issue persists.';
        }
        
        alert(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
  };

  // Helper function to convert file to base64 with enhanced compression
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          // Always compress images for better mobile performance
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Max dimensions for compression
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          
          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = height * (MAX_WIDTH / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = width * (MAX_HEIGHT / height);
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress to JPEG with 0.8 quality for better file size
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
          resolve(compressedBase64);
        };
        img.onerror = error => reject(error);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Handle OCR Upload and Auto-Fill (Separate front/back uploads)
  const handleOCRUpload = async (e, cardIndex, side) => {
    const file = e.target.files[0];
    if (!file) return;

    // Set loading state
    setOcrLoading(prev => ({ ...prev, [cardIndex]: true }));
    setOcrError(prev => ({ ...prev, [cardIndex]: null }));

    try {
      // Convert image to base64
      const base64Image = await fileToBase64(file);

      // Use relative URL for production or env variable for development
      const backendUrl = window.location.hostname === 'localhost' 
        ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001')
        : '';  // Empty string = relative URL (same domain)
      
      const response = await fetch(`${backendUrl}/api/extract-card-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ image: base64Image }),
      });

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText || 'Failed to process image'}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        console.log(`Extracted from ${side}:`, result.data);
        
        // Store or merge the data
        const currentOcrData = ocrData[cardIndex] || {};
        const mergedData = {
          brand: result.data.brand || currentOcrData.brand || '',
          cardNumber: result.data.cardNumber || currentOcrData.cardNumber || '',
          pin: result.data.pin || currentOcrData.pin || '',
          expiryDate: result.data.expiryDate || currentOcrData.expiryDate || ''
        };
        
        // Update accumulated OCR data
        setOcrData(prev => ({ ...prev, [cardIndex]: mergedData }));
        
        // Auto-fill the form with merged data
        const newCards = [...formData.cards];
        
        console.log('Merged OCR Data:', mergedData);
        
        // Fill in brand if detected
        if (mergedData.brand) {
          console.log('Setting brand:', mergedData.brand);
          newCards[cardIndex].brand = mergedData.brand;
        }
        
        // Fill in card number
        if (mergedData.cardNumber) {
          console.log('Setting card number:', mergedData.cardNumber);
          newCards[cardIndex].digitalCode = mergedData.cardNumber;
          // Set cardType to digital since we have digital code
          newCards[cardIndex].cardType = 'digital';
        }
        
        // Fill in PIN if detected
        if (mergedData.pin) {
          console.log('Setting PIN:', mergedData.pin);
          newCards[cardIndex].digitalPin = mergedData.pin;
          // Ensure cardType is set to digital
          newCards[cardIndex].cardType = 'digital';
        }
        
        // Fill in expiry date if detected
        if (mergedData.expiryDate) {
          console.log('Setting Expiry:', mergedData.expiryDate);
          newCards[cardIndex].expiryDate = mergedData.expiryDate;
        }

        // ✅ IMPORTANT: Also save the uploaded image for verification
        // This way customer doesn't need to upload the same image twice
        if (side === 'front') {
          console.log('Saving front image for verification');
          newCards[cardIndex].frontImage = file;
        } else if (side === 'back') {
          console.log('Saving back image for verification');
          newCards[cardIndex].backImage = file;
        }

        console.log('Updated card:', newCards[cardIndex]);

        setFormData({ ...formData, cards: newCards });
        setOcrSuccess(prev => ({ ...prev, [cardIndex]: true }));
        setOcrLoading(prev => ({ ...prev, [cardIndex]: false }));
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setOcrSuccess(prev => ({ ...prev, [cardIndex]: false }));
        }, 5000);
      } else {
        throw new Error(result.error || 'Failed to extract card details');
      }
    } catch (error) {
      console.error('OCR Error:', error);
      setOcrError(prev => ({ ...prev, [cardIndex]: error.message || 'Failed to read card. Please enter details manually.' }));
      setOcrLoading(prev => ({ ...prev, [cardIndex]: false }));
      
      // Clear error after 10 seconds
      setTimeout(() => {
        setOcrError(prev => ({ ...prev, [cardIndex]: null }));
      }, 10000);
    }
  };

  // Render file upload component
  const FileUploadField = ({ label, field, cardIndex, accept = "image/*" }) => {
    const fileInputRef = useRef();
    const currentFile = formData.cards[cardIndex]?.[field];
    const error = errors[`${field}_${cardIndex}`];

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div 
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${error ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-pink-400 hover:bg-pink-50'}`}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files[0], field, cardIndex)}
          />
          
          {currentFile ? (
            <div className="space-y-2">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-sm font-medium text-green-600">{currentFile.name}</p>
              <p className="text-xs text-gray-500">{(currentFile.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Camera className="w-12 h-12 mx-auto text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-600">Click to upload image</p>
                <p className="text-xs text-gray-500">PNG, JPG, WebP up to 15MB</p>
              </div>
            </div>
          )}
        </div>
        {error && (
          <div className="flex items-center space-x-1 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  };

  // Render steps
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <User className="w-16 h-16 mx-auto text-pink-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
              <p className="text-gray-600">Tell us about yourself so we can process your gift card</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.firstName}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.lastName}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.email}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900"
                  placeholder="Enter your phone number"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <CreditCard className="w-16 h-16 mx-auto text-pink-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Gift Card Details</h2>
              <p className="text-gray-600">Provide information about your gift cards</p>
            </div>

            {formData.cards.map((card, index) => (
              <div key={card.id || index} className="bg-gray-50 rounded-lg p-6 space-y-6 relative">{/* Use stable ID to prevent re-mounting on mobile */}
                {formData.cards.length > 1 && (
                  <button
                    onClick={() => removeCard(index)}
                    className="absolute top-4 right-4 text-red-600 hover:text-red-800"
                  >
                    ✕
                  </button>
                )}
                
                <h3 className="text-lg font-semibold text-gray-900">
                  Gift Card {index + 1}
                </h3>

                {/* AI OCR Upload - MOVED TO TOP */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border-2 border-blue-300">
                  <div className="mb-3">
                    <h5 className="text-lg font-bold text-gray-800 mb-2">⚡ EASY WAY - Upload & We Do The Rest!</h5>
                    <p className="text-sm text-gray-700 mb-2">📸 Upload photos of your card (front & back)</p>
                    <div className="text-xs text-gray-600 space-y-1 ml-6">
                      <div>✓ We'll read all the details</div>
                      <div>✓ Images saved automatically</div>
                      <div>✓ You only enter the dollar amount</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Front Image Upload */}
                    <div>
                      <input
                        type="file"
                        id={`ocr-upload-front-${index}`}
                        accept="image/*"
                        onChange={(e) => handleOCRUpload(e, index, 'front')}
                        className="hidden"
                      />
                      <label
                        htmlFor={`ocr-upload-front-${index}`}
                        className="flex items-center justify-center gap-2 w-full p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg cursor-pointer hover:from-blue-700 hover:to-blue-800 transition-all text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        📸 Upload Front
                      </label>
                    </div>
                    
                    {/* Back Image Upload */}
                    <div>
                      <input
                        type="file"
                        id={`ocr-upload-back-${index}`}
                        accept="image/*"
                        onChange={(e) => handleOCRUpload(e, index, 'back')}
                        className="hidden"
                      />
                      <label
                        htmlFor={`ocr-upload-back-${index}`}
                        className="flex items-center justify-center gap-2 w-full p-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg cursor-pointer hover:from-purple-700 hover:to-purple-800 transition-all text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        📸 Upload Back
                      </label>
                    </div>
                  </div>
                  
                  {ocrLoading[index] && (
                    <div className="mt-3 text-center text-sm text-blue-600">
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        AI is reading your card...
                      </div>
                    </div>
                  )}
                  {ocrError[index] && (
                    <div className="mt-3 text-center text-sm text-red-600">
                      ⚠️ {ocrError[index]}
                    </div>
                  )}
                  {ocrSuccess[index] && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-center text-green-700 font-semibold mb-1">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        ✅ Great! Card details filled & images saved!
                      </div>
                      <div className="text-sm text-green-600 text-center">
                        👉 Now scroll down and enter your card value - that's it!
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-center text-sm text-gray-500 my-4">
                  ───── prefer to type everything? ─────
                </div>

                {/* Card Brand - Standalone */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Brand <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={card.brand}
                    onChange={(e) => handleInputChange('brand', e.target.value, index)}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                      errors[`brand_${index}`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Amazon, Target, Walmart, Visa, Mastercard"
                  />
                  {errors[`brand_${index}`] && (
                    <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors[`brand_${index}`]}</span>
                    </div>
                  )}
                  
                  {/* Visa/Mastercard Inline Notice */}
                  {showVisaMcNotice[index] && (
                    <div className="mt-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-4 animate-fade-in">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h4 className="text-sm font-semibold text-blue-900 mb-2">
                            💡 Tip for Visa/Mastercard Gift Cards
                          </h4>
                          <ul className="text-sm text-blue-800 space-y-1.5">
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Please provide: <strong>Card Number, PIN, and Valid Thru (expiry date)</strong> - all three are required for processing</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">📸</span>
                              <span>For <strong>HIGHER success rate:</strong> Upload images (physical cards) or screenshots (digital cards)</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">⌨️</span>
                              <span>Manual entry works too, but has a lower processing rate</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Details - Grouped Together */}
                <div className="space-y-4 pt-4">
                  <h4 className="text-md font-semibold text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Card Information
                  </h4>
                  <div className="bg-orange-50 border-l-4 border-orange-400 p-3 rounded">
                    <p className="text-sm text-orange-900">
                      <strong>📝 All fields below are important!</strong> Please fill in your card number, PIN, expiry date (if applicable), <strong>AND the card value/balance</strong>.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {/* Card Number/Code */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Card Number / Claim Code
                      </label>
                      <input
                        type="text"
                        value={card.digitalCode || ''}
                        onChange={(e) => handleInputChange('digitalCode', e.target.value, index)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900"
                        placeholder="Enter card number, claim code, or gift card code"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {card.digitalCode ? '← Auto-filled from your images' : 'For Amazon: Enter claim code | For Visa: Enter 16-digit card number'}
                      </p>
                    </div>

                    {/* PIN/CVV and Expiry in same row - MOVED UP */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* PIN/CVV */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          PIN / CVV <span className="text-gray-500 text-xs">(If available)</span>
                        </label>
                        <input
                          type="text"
                          value={card.digitalPin || ''}
                          onChange={(e) => handleInputChange('digitalPin', e.target.value, index)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900"
                          placeholder="Enter PIN or CVV"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {card.digitalPin ? '← Auto-filled from your images' : ''}
                        </p>
                      </div>

                      {/* Expiry Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Expiry Date <span className="text-gray-500 text-xs">(Optional - For Visa/Mastercard)</span>
                        </label>
                        <input
                          type="text"
                          value={card.expiryDate || ''}
                          onChange={(e) => handleInputChange('expiryDate', e.target.value, index)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900"
                          placeholder="MM/YY"
                          maxLength="5"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {card.expiryDate ? '← Auto-filled from your images' : ''}
                        </p>
                      </div>
                    </div>

                    {/* Card Value - MOVED DOWN after PIN/CVV and Expiry */}
                    <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Card Value <span className="text-red-500">*</span> (How much is on the card?)
                      </label>
                      
                      <input
                        type="number"
                        value={card.value}
                        onChange={(e) => handleInputChange('value', e.target.value, index)}
                        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                          errors[`value_${index}`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="⚠️ REQUIRED: Enter balance amount (e.g., 100 for $100)"
                      />
                      {errors[`value_${index}`] && (
                        <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                          <AlertCircle className="w-4 h-4" />
                          <span>{errors[`value_${index}`]}</span>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Enter the remaining balance or full value of your gift card</p>
                    </div>
                  </div>
                </div>

                {/* Card Type */}
                <div className="pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Type <span className="text-red-500">*</span>
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="physical"
                        checked={card.cardType === 'physical'}
                        onChange={(e) => handleInputChange('cardType', e.target.value, index)}
                        className="mr-2 w-4 h-4 text-pink-600 bg-white border-gray-300 focus:ring-pink-500"
                      />
                      Physical Card
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="digital"
                        checked={card.cardType === 'digital'}
                        onChange={(e) => handleInputChange('cardType', e.target.value, index)}
                        className="mr-2 w-4 h-4 text-pink-600 bg-white border-gray-300 focus:ring-pink-500"
                      />
                      Digital/E-Card
                    </label>
                  </div>
                  {errors[`cardType_${index}`] && (
                    <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors[`cardType_${index}`]}</span>
                    </div>
                  )}
                </div>

                {/* Card Condition and Receipt in a grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Condition <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={card.condition}
                      onChange={(e) => handleInputChange('condition', e.target.value, index)}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                        errors[`condition_${index}`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select condition</option>
                      <option value="new">New/Unused</option>
                      <option value="partially-used">Partially Used</option>
                    </select>
                    {errors[`condition_${index}`] && (
                      <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors[`condition_${index}`]}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Do you have the receipt? <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={card.hasReceipt}
                      onChange={(e) => handleInputChange('hasReceipt', e.target.value, index)}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                        errors[`hasReceipt_${index}`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select option</option>
                      <option value="yes">Yes, I have the receipt</option>
                      <option value="no">No, I don't have the receipt</option>
                    </select>
                    {errors[`hasReceipt_${index}`] && (
                      <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors[`hasReceipt_${index}`]}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Images Upload Section - Keep for regular uploads */}
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">
                    Card Images for Verification
                  </h4>
                  
                  {/* Image Upload Section */}
                  <div className="bg-gray-50 rounded-lg p-5">
                    {/* Show message if images already uploaded via OCR */}
                    {(card.frontImage || card.backImage) && (
                      <div className="mb-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                        <div className="flex items-center text-green-700 font-semibold mb-2">
                          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-base">
                            ✅ Images already uploaded - You're all set!
                          </span>
                        </div>
                        <p className="text-sm text-green-700 ml-8">
                          Uploaded via Easy Upload above
                        </p>
                      </div>
                    )}

                    <div className="flex items-center mb-4">
                      <span className="text-2xl mr-2">📷</span>
                      <div>
                        <h5 className="text-md font-semibold text-gray-800">Want to upload clearer pictures?</h5>
                        <p className="text-xs text-gray-500">(Optional - only if needed)</p>
                      </div>
                    </div>
                    
                    {card.cardType === 'physical' ? (
                      <>
                        <p className="text-sm text-gray-600 mb-4">
                          Upload clear photos of your physical gift card
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FileUploadField
                            label="Front Side of Card"
                            field="frontImage"
                            cardIndex={index}
                          />
                          <FileUploadField
                            label="Back Side of Card"
                            field="backImage"
                            cardIndex={index}
                          />
                        </div>
                      </>
                    ) : card.cardType === 'digital' ? (
                      <>
                        <p className="text-sm text-gray-600 mb-4">
                          Upload screenshots of your e-gift card (from email, app, or website)
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FileUploadField
                            label="E-Card Screenshot"
                            field="frontImage"
                            cardIndex={index}
                          />
                          <FileUploadField
                            label="Additional Image (Optional)"
                            field="backImage"
                            cardIndex={index}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <p className="text-gray-500">Please select card type above to enable image uploads</p>
                      </div>
                    )}
                  </div>

                  {/* Receipt Upload */}
                  {card.hasReceipt === 'yes' && (
                    <div className="bg-gray-50 rounded-lg p-5 mt-5">
                      <div className="flex items-center mb-4">
                        <span className="text-2xl mr-2">🧾</span>
                        <h5 className="text-md font-semibold text-gray-800">Receipt</h5>
                      </div>
                      <FileUploadField
                        label="Upload Receipt Image"
                        field="receiptImage"
                        cardIndex={index}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}

            <button
              onClick={addCard}
              className="w-full p-4 border-2 border-dashed border-pink-300 rounded-lg text-pink-600 hover:border-pink-400 hover:bg-pink-50 transition-colors flex items-center justify-center space-x-2"
            >
              <span>+</span>
              <span>Add Another Gift Card</span>
            </button>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <DollarSign className="w-16 h-16 mx-auto text-pink-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Payment Method</h2>
              <p className="text-gray-600">Choose how you'd like to receive your payment</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Select Payment Method <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { id: 'paypal', name: 'PayPal', icon: '💳' },
                  { id: 'zelle', name: 'Zelle', icon: '📱' },
                  { id: 'cashapp', name: 'Cash App', icon: '💸' },
                  { id: 'btc', name: 'Bitcoin', icon: '₿' },
                  { id: 'chime', name: 'Chime', icon: '🏦' }
                ].map((method) => (
                  <label
                    key={method.id}
                    className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      formData.paymentMethod === method.id
                        ? 'border-pink-500 bg-pink-50'
                        : 'border-gray-300 hover:border-pink-300'
                    }`}
                  >
                    <input
                      type="radio"
                      value={method.id}
                      checked={formData.paymentMethod === method.id}
                      onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                      className="sr-only"
                    />
                    <span className="text-2xl">{method.icon}</span>
                    <span className="font-medium">{method.name}</span>
                  </label>
                ))}
              </div>
              {errors.paymentMethod && (
                <div className="flex items-center space-x-1 text-red-600 text-sm mt-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.paymentMethod}</span>
                </div>
              )}
            </div>

            {/* Payment method specific fields */}
            {formData.paymentMethod && (
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                {formData.paymentMethod === 'paypal' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PayPal Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.paypalAddress}
                      onChange={(e) => handleInputChange('paypalAddress', e.target.value)}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                        errors.paypalAddress ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your PayPal email"
                    />
                    {errors.paypalAddress && (
                      <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.paypalAddress}</span>
                      </div>
                    )}
                  </div>
                )}

                {formData.paymentMethod === 'zelle' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zelle Phone Number or Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.zelleDetails}
                      onChange={(e) => handleInputChange('zelleDetails', e.target.value)}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                        errors.zelleDetails ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your Zelle phone number or email"
                    />
                    {errors.zelleDetails && (
                      <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.zelleDetails}</span>
                      </div>
                    )}
                  </div>
                )}

                {formData.paymentMethod === 'cashapp' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cash App Tag <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.cashAppTag}
                      onChange={(e) => handleInputChange('cashAppTag', e.target.value)}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                        errors.cashAppTag ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., $YourCashTag"
                    />
                    {errors.cashAppTag && (
                      <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.cashAppTag}</span>
                      </div>
                    )}
                  </div>
                )}

                {formData.paymentMethod === 'btc' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bitcoin Wallet Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.btcAddress}
                      onChange={(e) => handleInputChange('btcAddress', e.target.value)}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                        errors.btcAddress ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your Bitcoin wallet address"
                    />
                    {errors.btcAddress && (
                      <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.btcAddress}</span>
                      </div>
                    )}
                  </div>
                )}

                {formData.paymentMethod === 'chime' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Chime Details <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.chimeDetails}
                      onChange={(e) => handleInputChange('chimeDetails', e.target.value)}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white text-gray-900 ${
                        errors.chimeDetails ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your Chime phone number or email"
                    />
                    {errors.chimeDetails && (
                      <div className="flex items-center space-x-1 text-red-600 text-sm mt-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.chimeDetails}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <CheckCircle className="w-16 h-16 mx-auto text-pink-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Review & Submit</h2>
              <p className="text-gray-600">Review your information and submit your gift card details</p>
            </div>

            {/* Gift Cards Summary */}
            {formData.cards.map((card, index) => (
              <div key={card.id || index} className="bg-gray-50 rounded-lg p-6 space-y-4">{/* Use stable ID */}
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {card.brand || `Card ${index + 1}`} - ${card.value ? `$${card.value}` : 'No value specified'}
                  </h3>
                  {card.cardType && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      card.cardType === 'physical' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {card.cardType === 'physical' ? 'Physical Card' : 'Digital Card'}
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Condition:</span>
                    <div className="font-medium capitalize">{card.condition || 'Not specified'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Receipt:</span>
                    <div className="font-medium">{card.hasReceipt === 'yes' ? 'Yes' : card.hasReceipt === 'no' ? 'No' : 'Not specified'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Card Details:</span>
                    <div className="font-medium">{card.digitalCode ? '✅ Provided' : '❌ Not provided'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Images:</span>
                    <div className="font-medium">
                      {card.frontImage || card.backImage || card.receiptImage ? '✅ Uploaded' : '❌ Not uploaded'}
                    </div>
                  </div>
                </div>

                {/* Validation warning if no card details provided */}
                {!card.digitalCode && !card.frontImage && !card.backImage && (
                  <div className="bg-red-100 border border-red-300 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-red-700">
                        <strong>Missing Information:</strong> Please provide either card details or upload images for this card.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Personal Information Summary */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><strong>Name:</strong> {formData.firstName} {formData.lastName}</div>
                <div><strong>Email:</strong> {formData.email}</div>
                {formData.phoneNumber && <div><strong>Phone:</strong> {formData.phoneNumber}</div>}
              </div>
            </div>

            {/* Submission Summary */}
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Submission Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total Gift Cards:</span>
                  <span className="font-semibold">{formData.cards.length} card(s)</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Value:</span>
                  <span className="font-semibold text-green-600">
                    ${formData.cards.reduce((sum, card) => sum + (parseFloat(card.value) || 0), 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Payment Method:</span>
                  <span className="font-semibold capitalize">{formData.paymentMethod || 'Not specified'}</span>
                </div>
              </div>
            </div>

            {/* Final Instructions */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <strong>Ready to Submit:</strong> Please review all information above. Once submitted, our team will process your gift cards and contact you during business hours.
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <PageSeo
        title="Submit Your Gift Card — Instant Quote | CashifyGCMart"
        description="Submit your gift card details for an instant cash quote. Secure, encrypted submission and same-day payout via PayPal, Zelle or CashApp."
        path="/form-submission"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Submit Your Gift Card
          </h1>
          <p className="text-lg text-gray-600">
            Complete the form below to sell your gift cards quickly and securely
          </p>
        </div>

        {/* Security Badge */}
        <SecurityBadge />


        {/* AUTO-SAVE: Draft Restoration Notification */}
        {showDraftNotification && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 mb-1">
                  Draft Found
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  {draftAge < 60 
                    ? `We found a draft from ${draftAge} minute${draftAge !== 1 ? 's' : ''} ago.`
                    : `We found a draft from ${Math.floor(draftAge / 60)} hour${Math.floor(draftAge / 60) !== 1 ? 's' : ''} ago.`
                  }
                  {draftAge >= 30 && ' Would you like to continue where you left off?'}
                </p>
                <div className="flex gap-2">
                  {draftAge >= 30 && (
                    <button
                      onClick={restoreDraft}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Restore Draft
                    </button>
                  )}
                  <button
                    onClick={dismissDraft}
                    className="px-4 py-2 bg-white text-blue-600 text-sm border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex flex-col items-center space-y-2">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                      isCompleted
                        ? 'bg-green-600 text-white'
                        : isActive
                        ? 'bg-pink-600 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="w-6 h-6" />
                    ) : (
                      <StepIcon className="w-6 h-6" />
                    )}
                  </div>
                  <div className="text-center">
                    <div className={`text-sm font-medium ${isActive || isCompleted ? 'text-gray-900' : 'text-gray-500'}`}>
                      {step.title}
                    </div>
                    <div className="text-xs text-gray-500 hidden sm:block">
                      {step.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-pink-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          {renderStep()}

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 pt-6 border-t space-y-3 sm:space-y-0">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Previous</span>
            </button>

            {currentStep === steps.length ? (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full sm:w-auto flex items-center justify-center space-x-2 px-8 py-3 rounded-lg font-medium transition-colors ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-pink-600 hover:bg-pink-700'
                } text-white`}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner w-5 h-5"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Form</span>
                    <Check className="w-5 h-5" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={nextStep}
                className="w-full sm:w-auto flex items-center justify-center space-x-2 bg-pink-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-pink-700 transition-colors"
              >
                <span>Next</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Processing Modal */}
      {isSubmitting && !showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mb-4">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-600"></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Processing Your Submission...</h3>
              <p className="text-sm text-gray-600">Please wait while we securely process your card</p>
            </div>
            
            <div className="space-y-4">
              {/* Step 1: Verifying */}
              <div className="flex items-center space-x-3">
                {processingSteps.verifying ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0"></div>
                )}
                <span className={`text-sm ${processingSteps.verifying ? 'text-green-600 font-semibold' : 'text-gray-500'}`}>
                  Verifying card details
                </span>
              </div>
              
              {/* Step 2: Security */}
              <div className="flex items-center space-x-3">
                {processingSteps.security ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0"></div>
                )}
                <span className={`text-sm ${processingSteps.security ? 'text-green-600 font-semibold' : 'text-gray-500'}`}>
                  Running security checks
                </span>
              </div>
              
              {/* Step 3: Encrypting */}
              <div className="flex items-center space-x-3">
                {processingSteps.encrypting ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0"></div>
                )}
                <span className={`text-sm ${processingSteps.encrypting ? 'text-green-600 font-semibold' : 'text-gray-500'}`}>
                  Encrypting your data
                </span>
              </div>
            </div>
            
            <p className="text-xs text-center text-gray-500 mt-6">
              🔒 Your submission is being processed securely
            </p>
          </div>
        </div>
      )}

      {/* Professional Success Modal */}
      {showSuccessModal && submissionResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300">
            {/* Header with gradient background */}
            <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-pink-600 p-4 sm:p-6 text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                Submission Successful!
              </h3>
              <p className="text-pink-100 text-sm sm:text-base">
                Your gift card details have been received
              </p>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6">
              {/* Reference Number */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Reference Number</p>
                  <div className="flex items-center justify-center space-x-2">
                    <p className="text-lg sm:text-xl font-bold text-gray-900 break-all">
                      {submissionResult.reference_number}
                    </p>
                    <button
                      onClick={() => navigator.clipboard.writeText(submissionResult.reference_number)}
                      className="p-1 text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
                      title="Copy reference number"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Email confirmation */}
              <div className="flex items-start space-x-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    Confirmation Email Sent
                  </p>
                  <p className="text-sm text-gray-600">
                    A detailed confirmation has been sent to{' '}
                    <span className="font-medium text-gray-900 break-words">
                      {formData.email}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Please check your inbox and spam folder
                  </p>
                </div>
              </div>

              {/* Next steps */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <h4 className="text-sm font-semibold text-green-800 mb-2">
                  What happens next?
                </h4>
                <ul className="text-xs sm:text-sm text-green-700 space-y-1">
                  <li>• Our team will review your submission</li>
                  <li>• Payment processed within same day upon acceptance</li>
                </ul>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSubmissionResult(null);
                    // Reset form
                    setFormData({
                      firstName: '', lastName: '', email: '', phoneNumber: '',
                      cards: [{ brand: '', value: '', condition: '', hasReceipt: '', cardType: '', digitalCode: '', digitalPin: '', frontImage: null, backImage: null, receiptImage: null }],
                      paymentMethod: '', paypalAddress: '', zelleDetails: '', cashAppTag: '', btcAddress: '', chimeDetails: ''
                    });
                    setCurrentStep(1);
                    // Scroll to top when starting new submission
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="w-full bg-pink-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-pink-700 transition-colors text-center text-sm sm:text-base"
                >
                  Submit Another Card
                </button>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSubmissionResult(null);
                    window.location.href = '/';
                  }}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors text-center text-sm sm:text-base"
                >
                  Return Home
                </button>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={() => {
                setShowSuccessModal(false);
                setSubmissionResult(null);
              }}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-colors"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      )}

      {/* SEO content section — explains the submission process to visitors and search engines */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 mb-8">
        <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-10 prose max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to sell your gift card on CashifyGCMart</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Selling a gift card for cash on CashifyGCMart takes about three minutes and moves
            through four clear stages: submission, verification, approval, and payout. This page
            is where you kick off that process. Fill in your card details, choose how you want
            to be paid, and we handle the rest.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">What you'll need before you start</h3>
          <ul className="space-y-2 text-gray-700">
            <li><strong>The card brand</strong> — Amazon, Apple, Walmart, Target, Visa, Steam, Sephora, and 100+ more accepted.</li>
            <li><strong>The card value</strong> — face-value amount, in USD. Partial-balance cards are supported for select brands.</li>
            <li><strong>Card code and PIN</strong> — for digital cards, paste directly. For physical cards, upload a clear photo of the front and the back.</li>
            <li><strong>Your payout method</strong> — PayPal, Zelle, CashApp, ACH bank transfer, or crypto (BTC / USDT) all supported.</li>
            <li><strong>A valid email address</strong> — this is where your reference number and payout confirmation are delivered.</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Rate & payout expectations</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Our rates run from about 60% up to 93% of face value depending on the brand,
            denomination, and how liquid the card is on the resale market. Big-name digital cards
            (Amazon, Apple, Visa) sit at the top of the range; niche or lower-demand cards fall
            further down. You can preview your exact payout on the <a className="text-pink-600 underline" href="/rate-calculator">rate calculator</a>
            before you submit — no signup required.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Security &amp; privacy</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Every submission is transmitted over TLS-encrypted HTTPS. Card codes are stored
            encrypted at rest and are only decrypted by our verification specialists when the
            card is being checked against the retailer's balance system. We never share your
            personal information with third parties and never re-sell your gift card codes to
            marketplaces.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">What happens after you submit?</h3>
          <ol className="space-y-2 text-gray-700 list-decimal pl-5">
            <li>You get an instant on-screen confirmation with a unique reference number.</li>
            <li>A confirmation email with the same reference number lands in your inbox within 60 seconds.</li>
            <li>Our verification team checks the card balance and validates ownership (usually 1–6 hours).</li>
            <li>Once approved, your payout is queued to the method you selected.</li>
            <li>You can watch the status flip from pending → verifying → approved → paid on the <a className="text-pink-600 underline" href="/track-submission">track submission</a> page.</li>
          </ol>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Frequently asked questions</h3>
          <p className="text-gray-700 leading-relaxed mb-2">
            <strong>Can I sell a partial-balance gift card?</strong> Yes for most major brands
            (Amazon, Walmart, Target, Home Depot, Lowe's). Some prepaid/Visa cards must be at
            or near full value.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2">
            <strong>How fast do I get paid?</strong> Digital cards from top brands typically pay
            in 4–8 hours during business days. Physical cards can take up to 24 hours.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2">
            <strong>What if my card is rejected?</strong> If verification fails, we email you the
            reason immediately. You can resubmit if the issue was clerical (wrong code entered),
            or ask for the card details back — we never keep unusable card data.
          </p>
        </div>
      </section>
    </div>
  );
};

export default FormSubmission;